/**
 * One piece of work in flight, as the dashboard's block and `/app/work` both state it.
 *
 * It was `DashboardPage.tsx`'s own until session 43 gave the block a destination.
 * Two pages now draw a `ClientWorkItemView`, and the whole point of the read model
 * answering one shape for all three kinds is that both draw it the same way — a
 * second copy of `nameOf` would be two names for one project the day somebody
 * changed one of them, which is what `.docs/19` §DRY watchlist is about.
 *
 * **It states facts and computes nothing.** The read model decided the stage, the
 * counts, the committed day and the day it last moved. What is left here is a
 * rendering choice the SPA owns and the API must not: spelling a month, and leaving
 * out a line for every null (session 41's T4 — a null is a line you leave out, never
 * "not scheduled" printed where a date would go).
 */

import type { ClientWorkItemView } from '@fareast/shared'

import { Lines } from '../portal/Layout.js'
import { B, Muted } from '../portal/Text.js'
import { useFormat } from '../../format/useFormat.js'

/**
 * `Lines` rather than two elements the caller arranges. `.b` and `.xs` are both
 * spans, and side by side in one flow they print as a single run — "Website —
 * casaturca.com rebuildBuild & content" — which is the defect `Lines`' own header
 * says has been found in three consecutive rounds of review. There is no arrangement
 * of `Lines` that renders the two on one line.
 */
export function WorkRow({ item }: { readonly item: ClientWorkItemView }) {
  const format = useFormat()
  const meta = metaLine(item, format)

  return (
    <Lines {...(meta === null ? {} : { under: <Muted>{meta}</Muted> })}>
      <B>{nameOf(item, format.monthLong)}</B>
    </Lines>
  )
}

type Formatters = ReturnType<typeof useFormat>

/**
 * What to call a piece of work.
 *
 * A project and a task each have a title of their own. A content plan has **no title
 * column**, so the read model sends its `period` and the page writes the month —
 * through `useFormat`, because spelling a month is a rendering decision and
 * `readmodels/client-work.ts` says so out loud. The ref is the fallback for the shape
 * the schema admits and the world has never produced: a content plan whose period is
 * null still has a name a client can read.
 *
 * A `switch` over the kind rather than "project, else plan", which is what it was
 * until session 43 added the third: a task would have fallen through to the content
 * plan's branch and been named after a month it does not have. Exhaustive, so a
 * fourth kind is a compile error here rather than a row named wrongly.
 */
export function nameOf(item: ClientWorkItemView, monthLong: Formatters['monthLong']): string {
  switch (item.kind) {
    case 'project':
    case 'task':
      return item.title
    case 'content_plan':
      return item.period === null ? item.ref : `${monthLong(item.period)} content plan`
  }
}

/**
 * Where it stands, when it is next due, and when it last moved — joined, and only the
 * parts there are.
 *
 * Built as a list and joined rather than written with separators between optional
 * pieces, because the second shape is how "Scheduled ·  · deliverables" reached every
 * row of the dashboard's own timeline: a separator printed for a value that was not
 * there. All three absent is `null`, which `WorkRow` renders as no second line at all.
 */
export function metaLine(item: ClientWorkItemView, format: Formatters): string | null {
  const parts: string[] = []

  const standing = standingOf(item, format.number)
  if (standing !== null) parts.push(standing)
  if (item.next_on !== null) parts.push(`next on ${format.day(item.next_on)}`)
  if (item.last_update !== null) parts.push(`last update ${format.day(item.last_update)}`)

  return parts.length === 0 ? null : parts.join(' · ')
}

/**
 * The one phrase that differs between the kinds.
 *
 * A project states its stage; a content plan states its pieces; a task states the
 * client's word for its state, which the read model chose out of `CLIENT_TASK_STAGES`
 * — so the two branches below are one branch, and that is the reason the projection
 * has one `stage` field rather than a shape per kind.
 *
 * **`published` is printed when it is zero**, which is the edge case session 41
 * names — "0 of 12" is where a month that has not started stands, and printing
 * nothing there says the plan does not exist. A project with no stage at all is a
 * real state too (`ClientWorkItemSchema.stage`), and it gets its title and no phrase.
 */
function standingOf(item: ClientWorkItemView, number: Formatters['number']): string | null {
  if (item.pieces !== null) {
    const { published, planned } = item.pieces
    const noun = planned === 1 ? 'piece' : 'pieces'
    return `${number(published)} of ${number(planned)} ${noun} published`
  }
  return item.stage === '' ? null : item.stage
}
